type RecommendationCalculationInput = {
  kilometers: number;
  buildYear: number;
  calculationTotal: number;
  listPrice: number;
  currentYear?: number;
};

export type RecommendationType = "repair" | "unsure" | "totaled";

type RecommendationCalculation = {
  ageYears: number;
  estimatedVehicleValue: number;
  costRatio: number; // bv. 0.27 = 27%
  repairScore: number; // 0–100
  recommendationType: RecommendationType;
  baseValue: number;
  expectedKm: number;
  kmAdjustment: number;
  baseScore: number;
  agePenalty: number;
  kmPenalty: number;
};

export function calculateRepairRecommendation({
  kilometers,
  buildYear,
  calculationTotal,
  listPrice,
  currentYear = new Date().getFullYear(),
}: RecommendationCalculationInput): RecommendationCalculation {
  const ageYears = Math.max(0, currentYear - buildYear);

  let baseValue: number;
  const initialDepriciation = 0.9;

  if (ageYears <= 0) {
    baseValue = listPrice * initialDepriciation;
  } else if (ageYears <= 3) {
    const rate1 = 0.78; // 22% per jaar
    baseValue = listPrice * rate1 ** ageYears;
  } else if (ageYears <= 7) {
    const rate1 = 0.78;
    const rate2 = 0.84;
    baseValue = listPrice * rate1 ** 3 * rate2 ** (ageYears - 3);
  } else {
    const rate1 = 0.78;
    const rate2 = 0.84;
    const rate3 = 0.7;
    baseValue = listPrice * rate1 ** 3 * rate2 ** 4 * rate3 ** (ageYears - 7);
  }

  // Sanity check: nooit negatief of 0
  baseValue = Math.max(1, baseValue);

  const expectedKm = Math.max(1, ageYears) * 25_000;
  const kmDelta = kilometers - expectedKm;
  const kmAdjustmentRaw = 1 - (kmDelta / 50_000) * 0.1;
  const kmAdjustment = Math.min(1.1, Math.max(0.7, kmAdjustmentRaw));

  let estimatedVehicleValue = Math.max(1, baseValue * kmAdjustment);

  estimatedVehicleValue = Math.min(estimatedVehicleValue, listPrice * 0.99);

  const costRatio = calculationTotal / estimatedVehicleValue;
  const baseScore = Math.max(0, Math.min(100, (1 - costRatio) * 100));

  const agePenalty = Math.max(0, ageYears - 5) * 4;
  const kmPenalty = (Math.max(0, kilometers - 140_000) / 30_000) * 4;

  const repairScore = Math.floor(
    Math.max(0, Math.min(100, baseScore - agePenalty - kmPenalty)),
  );

  let recommendationType: RecommendationType;
  if (repairScore >= 65) recommendationType = "repair";
  else if (repairScore >= 40) recommendationType = "unsure";
  else recommendationType = "totaled";

  return {
    ageYears,
    estimatedVehicleValue,
    costRatio,
    repairScore,
    recommendationType,
    baseValue,
    expectedKm,
    kmAdjustment,
    baseScore,
    agePenalty,
    kmPenalty,
  };
}
